import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { rxActions, RxActions } from '@rx-angular/state/actions';
import { ItineraryState } from '@vessel-itinerary/state/itinerary.state';
import { NoonService } from '@vessel-reports/noon/state/noon.service';
import { NoonState } from '@vessel-reports/noon/state/noon.state';
import { Bunker, InventorySupplyBody } from '@vessel-reports/noon/state/noon.types';
import { PopupService } from 'app/core/services/popup.service';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

interface INoonAction {
  createNewNoonReport: {
    body: any;
  };
  createNewNoonReportSuccess: void;
  getDetailNoonReport: {
    report_id: string;
  };
  getDetailNoonReportSuccess: void;
  updateDetailNoonReport: {
    body: any;
  };
  updateDetailNoonReportSuccess: void;
  getPreviousReport: {
    vessel_id: number;
    type?: number;
  };
  getPreviousReportSuccess: void;
  deleteNoonReport: {
    report_id: string;
    queryParams?: Params;
  };
  deleteNoonReportSuccess: void;
  updateReport: {
    report_id: string;
    body: any;
  };
  updateReportSuccess: void;
  submitNoonReport: {
    report_id: string;
  };
  submitNoonReportSuccess: void;
  createInventorySupply: {
    body: InventorySupplyBody;
  };
  createInventorySupplySuccess: void;
  updateInventorySupply: {
    id: string;
    body: InventorySupplyBody;
  };
  updateInventorySupplySuccess: void;
  deleteInventorySupply: {
    id: string;
  };
  deleteInventorySupplySuccess: void;
  getInventoriesSupply: {
    report_id: string;
  };
  createReportBunker: {
    body: any;
  };
  createReportBunkerSuccess: void;
  updateReportBunker: {
    report_id: string;
    body: Bunker;
  };
  updateReportBunkerSuccess: void;
  deleteReportBunker: {
    report_id: string;
  };
  deleteReportBunkerSuccess: void;
  getBunker: {
    bunker_id: string;
  };
  getAdvanceFilters: {
    key?: 'noon_report_type_id' | 'voyage_number' | 'date_report';
    limit?: number;
    queryParams?: Params;
  };
  getAdvanceFiltersSuccess: {
    key?: 'noon_report_type_id' | 'voyage_number' | 'date_report';
    types?: any[];
    voyages?: any[];
    date_report?: any;
  };
  cleanState: any;
}

@Injectable({
  providedIn: 'root'
})
export class NoonActions {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  actions: RxActions<INoonAction> = rxActions<INoonAction>();
  private readonly _noonService: NoonService = inject(NoonService);
  private readonly _state: NoonState = inject(NoonState);
  private readonly _itineraryState: ItineraryState = inject(ItineraryState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToCreateNewNoonReport$();
    this._subscribeToGetDetailNoonReport$();
    this._subscribeToUpdateDetailReport$();
    this._subscribeToGetPreviousReport$();
    this._subscribeToDeleteReport$();
    this._subscribeToUpdateReport$();
    this._subscribeToSubmitNoonReport$();
    this._createInventorySupply$();
    this._updateInventorySupply$();
    this._deleteInventorySupply$();
    this._subscribeToGetInventoriesSupply$();
    this._subscribeToCreateReportBunker$();
    this._subscribeToDeleteReportBunker$();
    this._subscribeToUpdateReportBunker$();
    this._subscribeToGetBunker$();
    this._subscribeToGetAdvanceFilter$();
    this._subscribeToCleanState$();
  }

  private _subscribeToCreateNewNoonReport$() {
    this.actions.onCreateNewNoonReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.createNewReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.createNewReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.noon_report_created_successfully')
        });

        this._state.set({
          detailNoonReport: response?.data
        });
        this.actions.createNewNoonReportSuccess();
      }
    );
  }

  private _subscribeToGetDetailNoonReport$() {
    this.actions.onGetDetailNoonReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.getDetailNoonReport>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._noonService.getDetailNoonReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return this.actions.getDetailNoonReportSuccess();
        }
        this._state.set({
          detailNoonReport: response?.data
        });
        this.actions.getDetailNoonReportSuccess();
      }
    );
  }

  private _subscribeToUpdateDetailReport$() {
    this.actions.onUpdateDetailNoonReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.updateDetailReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.updateDetailReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.noon_detail_report_updated_successfully')
        });

        this.actions.updateDetailNoonReportSuccess();
      }
    );
  }

  private _subscribeToGetPreviousReport$() {
    this.actions.onGetPreviousReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.getPreviousReport>;
        return payload$.pipe(
          switchMap((payload) => this._noonService.getPreviousReport(payload.vessel_id, payload.type).pipe<APIResponse>(catchError((error) => of(error))))
        );
      },
      (response: any) => {
        if (response?.error) {
          return this.actions.getPreviousReportSuccess();
        }
        this._state.set({
          previousReport: response?.data
        });
        this.actions.getPreviousReportSuccess();
      }
    );
  }

  private _subscribeToDeleteReport$() {
    this.actions.onDeleteNoonReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.deleteReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.deleteReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.noon_report_deleted_successfully')
        });
        this.actions.deleteNoonReportSuccess();
      }
    );
  }

  private _subscribeToUpdateReport$() {
    this.actions.onUpdateReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.updateReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.updateReport(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.noon_report_updated_successfully')
        });
        this.actions.updateReportSuccess();
      }
    );
  }

  private _subscribeToSubmitNoonReport$() {
    this.actions.onSubmitNoonReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.submitReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.submitReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.noon_report_submitted_successfully')
        });
        this.actions.submitNoonReportSuccess();
      }
    );
  }

  private _createInventorySupply$() {
    this.actions.onCreateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.createInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.createInventorySupply(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_created_successfully')
        });
        this.actions.createInventorySupplySuccess(response?.data);
      }
    );
  }

  private _updateInventorySupply$() {
    this.actions.onUpdateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.updateInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.updateInventorySupply(payload.id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_updated_successfully')
        });
        this.actions.updateInventorySupplySuccess(response?.data);
      }
    );
  }

  private _deleteInventorySupply$() {
    this.actions.onDeleteInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.deleteInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._noonService.deleteInventorySupply(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_deleted_successfully')
        });
        this.actions.deleteInventorySupplySuccess(response?.data);
      }
    );
  }

  private _subscribeToGetInventoriesSupply$() {
    this.actions.onGetInventoriesSupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.getInventoriesSupply>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._noonService.getInventoriesSupply(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._itineraryState.set({
          vesselMovements: response?.data
        });
      }
    );
  }

  private _subscribeToCreateReportBunker$() {
    this.actions.onCreateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.createReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._noonService.createReportBunker(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_created_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.createReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToUpdateReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.updateReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._noonService.updateReportBunker(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_updated_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.updateReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToDeleteReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.deleteReportBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._noonService.deleteReportBunker(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_deleted_successfully')
        });

        this.actions.deleteReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToGetBunker$() {
    this.actions.onGetBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.getBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._noonService.getBunker(payload.bunker_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._state.set({
          detailBunker: response.data
        });
      }
    );
  }

  private _subscribeToGetAdvanceFilter$(): void {
    this.actions.onGetAdvanceFilters(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._noonService.getAdvanceFilters>;

        return payload$.pipe(
          switchMap((payload) =>
            this._noonService.getAdvanceFilters(payload.key, payload.limit, payload.queryParams).pipe<APIResponse, APIResponse, any>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({ payload, response }))
            )
          )
        );
      },
      ({ payload, response }: any) => {
        if (payload?.key === 'noon_report_type_id') {
          this._state.set({
            types: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ types: response?.data, key: payload?.key });
        } else if (payload?.key === 'voyage_number') {
          this._state.set({
            types: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ voyages: response?.data, key: payload?.key });
        } else {
          this._state.set({
            dateReport: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ date_report: response?.data, key: payload?.key });
        }
      }
    );
  }

  private _subscribeToCleanState$() {
    this.actions.cleanState$.subscribe(() => {
      this._state.set({
        detailBunker: null,
        inventories: []
      });
    });
  }
}
